import React from 'react';
import { useStore } from '../../../lib/store';
import { CURRENCIES } from '../../../../constants';

/**
 * Shared look and helpers for CartAssistant chart cards: one brand gradient
 * (emerald → cyan → indigo) for lines, areas and bars, plus a categorical
 * palette from the same family for pie slices.
 */
export const CHART_GRADIENT_STOPS = [
  { offset: 0, color: '#10b981' },   // emerald-500
  { offset: 0.5, color: '#06b6d4' }, // cyan-500
  { offset: 1, color: '#6366f1' },   // indigo-500
] as const;

/** CSS equivalent for HTML bars. */
export const CHART_GRADIENT_CSS = `linear-gradient(90deg, ${CHART_GRADIENT_STOPS.map(s => `${s.color} ${s.offset * 100}%`).join(', ')})`;

export const CHART_CATEGORY_PALETTE = [
  '#10b981', '#06b6d4', '#6366f1', '#f59e0b', '#ec4899', '#8b5cf6', '#14b8a6', '#f97316', '#0ea5e9', '#84cc16',
];

const hexToRgb = (hex: string) => [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16));

/** Gradient color at position t ∈ [0, 1] — tints data points to match the line under them. */
export const chartColorAt = (t: number): string => {
  const clamped = Math.min(1, Math.max(0, Number.isFinite(t) ? t : 0));
  const stops = CHART_GRADIENT_STOPS;
  const upper = stops.findIndex(s => s.offset >= clamped);
  if (upper <= 0) return stops[0].color;
  const a = stops[upper - 1];
  const b = stops[upper];
  const local = (clamped - a.offset) / (b.offset - a.offset);
  const [r1, g1, b1] = hexToRgb(a.color);
  const [r2, g2, b2] = hexToRgb(b.color);
  const mix = (x: number, y: number) => Math.round(x + (y - x) * local);
  return `rgb(${mix(r1, r2)}, ${mix(g1, g2)}, ${mix(b1, b2)})`;
};

/** Unique SVG gradient ids per chart instance — several charts can share one chat thread. */
export const useChartGradientIds = () => {
  const id = React.useId().replace(/:/g, '');
  return { line: `chart-line-${id}`, area: `chart-area-${id}`, fade: `chart-fade-${id}`, mask: `chart-mask-${id}` };
};

/**
 * <defs> for a left→right gradient line and a matching area fill that fades
 * toward the baseline. Uses userSpaceOnUse so a perfectly flat line (zero-height
 * bounding box) still renders — objectBoundingBox gradients make it disappear.
 */
export const ChartGradientDefs: React.FC<{
  ids: ReturnType<typeof useChartGradientIds>;
  x1: number;
  x2: number;
  top: number;
  bottom: number;
}> = ({ ids, x1, x2, top, bottom }) => (
  <defs>
    <linearGradient id={ids.line} gradientUnits="userSpaceOnUse" x1={x1} y1={0} x2={x2} y2={0}>
      {CHART_GRADIENT_STOPS.map(s => (
        <stop key={s.offset} offset={`${s.offset * 100}%`} stopColor={s.color} />
      ))}
    </linearGradient>
    <linearGradient id={ids.area} gradientUnits="userSpaceOnUse" x1={x1} y1={0} x2={x2} y2={0}>
      {CHART_GRADIENT_STOPS.map(s => (
        <stop key={s.offset} offset={`${s.offset * 100}%`} stopColor={s.color} />
      ))}
    </linearGradient>
    <linearGradient id={ids.fade} gradientUnits="userSpaceOnUse" x1={0} y1={top} x2={0} y2={bottom}>
      <stop offset="0%" stopColor="white" stopOpacity="0.28" />
      <stop offset="100%" stopColor="white" stopOpacity="0" />
    </linearGradient>
    <mask id={ids.mask} maskUnits="userSpaceOnUse">
      <rect x={x1 - 20} y={top - 20} width={x2 - x1 + 40} height={bottom - top + 40} fill={`url(#${ids.fade})`} />
    </mask>
  </defs>
);

/**
 * Currency symbol for chart cards: the user's chosen currency wins, then the
 * symbol the model passed (cards historically used either prop name), then '$'.
 */
export const useChartCurrency = (props?: { currencySymbol?: string; activeCurrencySymbol?: string }) => {
  const { userProfile, currency } = useStore();
  const userCurrency = currency || userProfile?.settings?.interface?.currency;
  const propSymbol = props?.currencySymbol || props?.activeCurrencySymbol;
  return React.useMemo(
    () => CURRENCIES.find(c => c.code === userCurrency)?.symbol || propSymbol || '$',
    [userCurrency, propSymbol],
  );
};

/** Parses 'YYYY-MM-DD' as a local date — `new Date('2026-08-05')` is UTC midnight and shows Aug 4 in the Americas. */
export const parseChartDate = (value: string): Date => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value?.trim() ?? '');
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value);
};

/** Pointer, touch and keyboard selection shared by the assistant's line charts. */
export const useChartPointInteraction = (count: number) => {
  const [selected, setSelected] = React.useState<number | null>(null);
  const activePoint = selected !== null && selected < count ? selected : null;
  const pointProps = (index: number, label: string) => ({
    role: 'button' as const,
    tabIndex: 0,
    'data-chart-point': true,
    'aria-label': label,
    'aria-pressed': activePoint === index,
    onFocus: () => setSelected(index),
    onBlur: () => setSelected(null),
    onClick: () => setSelected(index),
    onPointerEnter: (event: React.PointerEvent<SVGGElement>) => { if (event.pointerType !== 'touch') setSelected(index); },
    onPointerLeave: (event: React.PointerEvent<SVGGElement>) => {
      if (event.pointerType !== 'touch' && event.currentTarget !== document.activeElement) setSelected(null);
    },
    onKeyDown: (event: React.KeyboardEvent<SVGGElement>) => {
      if (event.key === 'Escape') { event.preventDefault(); setSelected(null); return; }
      if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setSelected(index); return; }
      const target = event.key === 'ArrowRight' ? Math.min(count - 1, index + 1)
        : event.key === 'ArrowLeft' ? Math.max(0, index - 1)
        : event.key === 'Home' ? 0 : event.key === 'End' ? count - 1 : null;
      if (target !== null) {
        event.preventDefault();
        event.currentTarget.parentElement?.querySelectorAll<SVGGElement>('[data-chart-point]')[target]?.focus();
        setSelected(target);
      }
    },
  });
  return { activePoint, pointProps, clearSelection: () => setSelected(null) };
};
